import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  Divider,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  IconButton,
  Link,
  Image,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import logoVertical from '../assets/logo/aplica-ia-logo-vertical.svg';
import { useAuth } from '../contexts/AuthContext';
import ForgotPasswordModal from '../components/ForgotPasswordModal';

const Login: React.FC = () => {
  const [forgotOpen, setForgotOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('gray.50', 'gray.700');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    const result = await login(email, password);
    if (!result.success) {
      setError(result.message);
    } else {
      navigate('/inicio'); // evita ficar em /login (rota inexistente no app) -> tela branca
    }
  };

  return (
    <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center" p={4}>
      <Container maxW="md">
        <Card bg={cardBg} shadow="xl">
          <CardBody p={8}>
            <VStack spacing={6} align="center">
              {/* Header */}
              <VStack spacing={3}>
                <Image src={logoVertical} alt="Aplica IA" w="150px" borderRadius="xl" />
                <Text color="gray.600" textAlign="center" fontSize="sm">
                  Converse com seus documentos usando IA
                </Text>
              </VStack>

              <Divider />

              <Box w="100%">
                <form onSubmit={handleSubmit}>
                  <VStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Email</FormLabel>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="seu@email.com"
                        bg={inputBg}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Senha</FormLabel>
                      <InputGroup>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="sua senha"
                          bg={inputBg}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                            icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                            onClick={() => setShowPassword(!showPassword)}
                            variant="ghost"
                            size="sm"
                          />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    {error && (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        {error}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      colorScheme="brand"
                      size="lg"
                      width="100%"
                      isLoading={isLoading}
                      loadingText="Entrando..."
                    >
                      Entrar
                    </Button>

                    <Link alignSelf="center" fontSize="sm" color="brand.600" onClick={() => setForgotOpen(true)} cursor="pointer">
                      Esqueci minha senha
                    </Link>
                  </VStack>
                </form>
              </Box>

              <Divider />

              <Text color="gray.500" fontSize="sm">
                Não tem acesso?{' '}
                <Link as={RouterLink} to="/register" color="brand.600" fontWeight="semibold">
                  Solicitar cadastro
                </Link>
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </Container>

      <ForgotPasswordModal isOpen={forgotOpen} onClose={() => setForgotOpen(false)} />
    </Box>
  );
};

export default Login;
